import React, { useState } from 'react';
import * as Tooltip from '@radix-ui/react-tooltip';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const truncate = (str, max = 20) =>
  str.length > max ? str.substring(0, max) + '…' : str;

const ItemsChartsSection = ({ data = [], totalOrders = 0 }) => {
  const [limit, setLimit] = useState(10);

  const handleLimitChange = (e) => {
    const val = parseInt(e.target.value, 10);
    setLimit(val);
  };

  const displayedData = data.slice(0, limit).map(item => ({
    ...item,
    name: truncate(item.name)
  }));

  const totalItems = data.reduce((sum, item) => sum + Number(item.total_quantity || 0), 0);
  const avgItemsPerTicket = Number(totalOrders) > 0 ? totalItems / Number(totalOrders) : null;

  const mostOrderedItem = data.reduce(
    (max, item) =>
      Number(item.total_quantity || 0) > Number(max.total_quantity || 0) ? item : max,
    { name: '', total_quantity: 0 }
  );

  const minQty = Math.min(...data.map(item => Number(item.total_quantity || Infinity)));

  const leastOrderedItems = data.filter(
    item => Number(item.total_quantity || 0) === minQty
  );
  

  return (
    <div className="border rounded p-4 bg-white dark:bg-gray-900 dark:border-gray-700 shadow-sm dark:text-gray-100">
     

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800 rounded-lg p-4 shadow-sm">
          <h4 className="text-sm text-blue-700 dark:text-blue-300 font-medium">Total Items Ordered</h4>
          <p className="text-2xl text-blue-900 dark:text-blue-200 font-bold mt-1">{totalItems.toLocaleString()}</p>
        </div>

        <div className="bg-green-50 border border-green-200 dark:bg-green-900/20 dark:border-green-800 rounded-lg p-4 shadow-sm">
          <h4 className="text-sm text-green-700 dark:text-green-300 font-medium">Most Ordered Item</h4>
          <p className="text-md text-blue-900 dark:text-blue-200 font-semibold truncate">{mostOrderedItem.name || '—'}</p>
          <p className="text-lg text-blue-900 dark:text-blue-200 font-bold">{Number(mostOrderedItem.total_quantity || 0).toLocaleString()}</p>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800 rounded-lg p-4 shadow-sm">
  <h4 className="text-sm text-yellow-700 dark:text-yellow-300 font-medium">Least Ordered Item(s)</h4>

  <p className="text-md text-blue-900 dark:text-blue-200 font-semibold truncate">
    {leastOrderedItems.length === 1
      ? leastOrderedItems[0].name
      : `${leastOrderedItems.length} items`}
  </p>

  <p className="text-lg text-blue-900 dark:text-blue-200 font-bold">
    {Number(minQty).toLocaleString()}
  </p>

  {leastOrderedItems.length > 1 && (
    <Tooltip.Provider delayDuration={200}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <span className="text-xs text-yellow-800 dark:text-yellow-300 underline cursor-help">
            View all
          </span>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content className="bg-black text-white text-xs rounded px-3 py-2 shadow-md z-50 max-w-[200px]">
            {leastOrderedItems.map((item, index) => (
              <div key={index}>{item.name}</div>
            ))}
            <Tooltip.Arrow className="fill-black" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  )}
</div>


        <div className="bg-purple-50 border border-purple-200 dark:bg-purple-900/20 dark:border-purple-800 rounded-lg p-4 shadow-sm">
          <h4 className="text-sm text-purple-700 dark:text-purple-300 font-medium">Avg. Items Per Ticket</h4>
          <p className="text-2xl text-blue-900 dark:text-blue-200 font-bold mt-1">
            {avgItemsPerTicket == null ? '—' : avgItemsPerTicket.toFixed(1)}
          </p>
        </div>
      </div>
      
       {/* Header & Filter */}
       <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <h3 className="text-blue-800 dark:text-blue-300 font-semibold text-lg">MOST COMMON ITEMS</h3>
          <Tooltip.Provider delayDuration={300}>
            <Tooltip.Root>
              <Tooltip.Trigger asChild>
                <span className="text-gray-400 cursor-pointer text-sm">ⓘ</span>
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content
                  className="bg-black text-white text-xs rounded px-3 py-2 shadow-md z-50 max-w-[220px]"
                  sideOffset={5}
                >
                  <div><strong>Total Quantity:</strong> Total number of individual units ordered (e.g., 3x Fries = 3).</div>
                  <div className="mt-1"><strong>Orders Count:</strong> Number of unique orders that included the item.</div>
                  <Tooltip.Arrow className="fill-black" />
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
          </Tooltip.Provider>
        </div>

        <select
          value={limit}
          onChange={handleLimitChange}
          className="border rounded px-2 py-1 text-sm bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
        >
          <option value={10}>Top 10</option>
          <option value={20}>Top 20</option>
          <option value={data.length}>Show All</option>
        </select>
      </div>

      {/* Chart */}
      {displayedData.length > 0 ? (
        <ResponsiveContainer width="100%" height={450}>
          <BarChart
            data={displayedData}
            layout="vertical"
            margin={{ top: 10, right: 30, left: 100, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" />
            <YAxis
              dataKey="name"
              type="category"
              width={200}
              tick={{ fontSize: 13, fontWeight: 500 }}
            />
            <RechartsTooltip />
            <Legend verticalAlign="top" height={36} />
            <Bar dataKey="total_quantity" fill="#4A90E2" name="Total Quantity" />
            <Bar dataKey="distinct_orders" fill="#82ca9d" name="Orders Count" />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <p className="text-sm text-gray-500 dark:text-gray-400">No item data available.</p>
      )}

      {/* Table */}
      <div className="mt-6 overflow-x-auto">
        <table className="min-w-full text-sm border border-gray-200 dark:border-gray-700">
          <thead className="bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-100">
            <tr>
              <th className="px-4 py-2 text-left border dark:border-gray-600">Item Name</th>
              <th className="px-4 py-2 text-right border dark:border-gray-600">
                <Tooltip.Provider delayDuration={200}>
                  <Tooltip.Root>
                    <Tooltip.Trigger asChild>
                      <span className="cursor-help">Total Quantity ⓘ</span>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                      <Tooltip.Content className="bg-black text-white text-xs rounded px-2 py-1 shadow-lg z-50">
                        Total number of units ordered
                        <Tooltip.Arrow className="fill-black" />
                      </Tooltip.Content>
                    </Tooltip.Portal>
                  </Tooltip.Root>
                </Tooltip.Provider>
              </th>
              <th className="px-4 py-2 text-right border dark:border-gray-600">
                <Tooltip.Provider delayDuration={200}>
                  <Tooltip.Root>
                    <Tooltip.Trigger asChild>
                      <span className="cursor-help">Orders Count ⓘ</span>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                      <Tooltip.Content className="bg-black text-white text-xs rounded px-2 py-1 shadow-lg z-50">
                        Number of unique orders the item appeared in
                        <Tooltip.Arrow className="fill-black" />
                      </Tooltip.Content>
                    </Tooltip.Portal>
                  </Tooltip.Root>
                </Tooltip.Provider>
              </th>
              <th className="px-4 py-2 text-right border dark:border-gray-600">
                <Tooltip.Provider delayDuration={200}>
                  <Tooltip.Root>
                    <Tooltip.Trigger asChild>
                      <span className="cursor-help">Avg. Bump Time ⓘ</span>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                      <Tooltip.Content className="bg-black text-white text-xs rounded px-2 py-1 shadow-lg z-50">
                        Average time from order received to bump (in minutes)
                        <Tooltip.Arrow className="fill-black" />
                      </Tooltip.Content>
                    </Tooltip.Portal>
                  </Tooltip.Root>
                </Tooltip.Provider>
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, i) => (
              <tr key={i} className="even:bg-gray-50 dark:bg-gray-800 dark:even:bg-gray-700/80">
                <td className="px-4 py-2 border dark:border-gray-700">{item.name}</td>
                <td className="px-4 py-2 text-right border dark:border-gray-700">{item.total_quantity}</td>
                <td className="px-4 py-2 text-right border dark:border-gray-700">{item.distinct_orders}</td>
                <td className="px-4 py-2 text-right border dark:border-gray-700">
                  {!isNaN(Number(item.average_bump_time_seconds))
                    ? `${(Number(item.average_bump_time_seconds) / 60).toFixed(1)} min`
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ItemsChartsSection;
